/**
 * @module support/api
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

import { PaymentInformation } from '@support/payment/online-payment/models/card-entry-form.model';
import {
  Address,
  Amount,
  Expiration,
  PaymentMethod,
} from '@support/api/proxies/requests/api-2150.interface';
import { SHARED_PAYMENT } from '@support/payment/shared-payment/constants/shared-payment.constants';
import { DEVICE_ASSESSSMENT_CONSTANT } from '@experience/device-assessment/device-assessment.constants';
import { CardAddressModel } from '@experience/shop/order-payment/online-order-payment/credit-card-address/models/credit-card-address.model';
import { CART_REVIEW_CONST } from '@experience/shop/cart/constants/cart-review.constants';

export class OnlineSubmitOrder {
  /**
   * To store cardId
   */
  public cartId: string;
  /**
   * To store actionCode
   */
  public actionCode: string;
  /**
   * To store payment method details
   */
  public paymentMethod: PaymentMethod;
  /**
   * To check TermsAndCondition
   */
  public termsAndConditionsAccepted: boolean;
  /**
   *  To check byPassFraudCheck
   */
  public bypassFraudCheck: boolean;
  /**
   *  To store bypassInventoryCheck
   */
  public bypassInventoryCheck: boolean;
  /**
   *  To store isAcceptable
   */
  public isAcceptable: boolean;
  /**
   *  To store orderId
   */
  public orderId: string;
  /**
   *  To store orderType
   */
  public orderType: string;
  /**
   *  To store transactionType
   */
  public transactionType: string;
  /**
   * To store fingerPrintId
   */
  public fingerPrintId: string;

  constructor(
    cartId: string,
    orderType: string,
    amount: string = DEVICE_ASSESSSMENT_CONSTANT.NUMBERZERO,
    paymentInformation?: PaymentInformation,
    address?: CardAddressModel,
    orderReason?: string,
    bypassFraudCheck: boolean = false,
    bypassInventoryCheck: boolean = false,
    isAcceptable: boolean = false,
    fingerprintId?: string
  ) {
    this.cartId = cartId;
    this.fingerPrintId = fingerprintId;
    this.orderId = cartId;
    this.bypassFraudCheck = bypassFraudCheck;
    this.bypassInventoryCheck = bypassInventoryCheck;
    this.isAcceptable = isAcceptable;
    this.termsAndConditionsAccepted = true;
    if (orderType === CART_REVIEW_CONST.EXCHANGE && orderReason) {
      this.actionCode = SHARED_PAYMENT.ADD_SUBMIT;
    } else {
      this.actionCode =
        parseFloat(amount) <= 0 ||
        bypassFraudCheck ||
        this.bypassInventoryCheck ||
        orderType === DEVICE_ASSESSSMENT_CONSTANT.RETURN_REASON
          ? SHARED_PAYMENT.SUBMIT
          : SHARED_PAYMENT.ADD_SUBMIT;
      this.orderType = orderType;
      if (paymentInformation) {
        this.setPaymentMethod(paymentInformation, amount, address);
      }
    }
    this.orderType = orderType;
    this.getTransactionType(orderType, paymentInformation);
  }

  public getTransactionType(orderType: string, paymentInformation?: PaymentInformation): void {
    if (!paymentInformation) {
      this.paymentMethod = {};
    }
    if (orderType.toUpperCase() === SHARED_PAYMENT.RETURN) {
      this.transactionType = SHARED_PAYMENT.REFUND;
      this.paymentMethod.transactionType = SHARED_PAYMENT.REFUND;
    } else {
      this.transactionType = orderType;
      if (orderType === DEVICE_ASSESSSMENT_CONSTANT.ACTIVATION) {
        this.paymentMethod.transactionType = SHARED_PAYMENT.PAYMENT;
      }
    }
  }

  /**
   * Function to get Payment Method
   * @returns PaymentMethod
   */
  // tslint:disable-next-line:cyclomatic-complexity
  public setPaymentMethod(
    paymentInformation: PaymentInformation,
    amount: string,
    address?: CardAddressModel
  ): void {
    if (!paymentInformation.paymentCardAlias) {
      this.paymentMethod = {
        paymentId: paymentInformation.paymentId,
        paymentMethodCode: paymentInformation.paymentMethodCode,
        securityCode: paymentInformation.encryptedCvv
          ? paymentInformation.encryptedCvv
          : paymentInformation.cvv,
        pinLessIndicator: paymentInformation.pinLessIndicator,
        source: SHARED_PAYMENT.JPAY,
        method: paymentInformation.paymentMethodCode,
        typeCode: paymentInformation.cardType,
        saveForFuture: paymentInformation.saveForFuture,
        transactionType: SHARED_PAYMENT.PAYMENT,
        storePaymentMethodIndicator: paymentInformation.storePaymentMethodIndicator,
        nameOnCard: paymentInformation.fullName,
        cardNumber: paymentInformation.cardNumber
          ? this.maskCardNumber(paymentInformation.cardNumber)
          : '',
        cardPresentIndicator: false,
        expiration: this.setExpiration(paymentInformation),
        chargeAccountNumberIndicator: paymentInformation.chargeAccountNumberIndicator,
        amount: this.setAmount(amount),
        enableAutopay: paymentInformation.enableAutopay,
      };
      this.paymentMethod.token = {
        encryptedContent: paymentInformation.token
          ? paymentInformation.token
          : paymentInformation.encryptedCardNumber,
        encryptionTarget: SHARED_PAYMENT.PAN,
      };
    } else {
      this.paymentMethod = {
        paymentId: paymentInformation.paymentId,
        paymentMethodCode: paymentInformation.paymentMethodCode,
        securityCode: paymentInformation.encryptedCvv
          ? paymentInformation.encryptedCvv
          : paymentInformation.cvv,
        pinLessIndicator: paymentInformation.pinLessIndicator,
        source: SHARED_PAYMENT.JPAY,
        method: paymentInformation.paymentMethodCode,
        typeCode: paymentInformation.cardType,
        saveForFuture: paymentInformation.saveForFuture,
        transactionType: SHARED_PAYMENT.PAYMENT,
        storePaymentMethodIndicator: paymentInformation.storePaymentMethodIndicator,
        nameOnCard: paymentInformation.fullName,
        cardNumber: paymentInformation.cardNumber,
        token: {},
        paymentCardAlias: paymentInformation.paymentCardAlias,
        cardPresentIndicator: false,
        expiration: this.setExpiration(paymentInformation),
        chargeAccountNumberIndicator: paymentInformation.chargeAccountNumberIndicator,
        amount: this.setAmount(amount),
        enableAutopay: paymentInformation.enableAutopay,
      };
    }
    if (paymentInformation && paymentInformation.addressId) {
      this.paymentMethod.addressId = paymentInformation.addressId;
    } else {
      this.paymentMethod.address = this.setAddress(
        paymentInformation.zipCode,
        paymentInformation.country,
        address
      );
    }
  }

  /**
   * Function to set Expiration
   * @returns Expiration
   */
  private setExpiration(paymentInformation: PaymentInformation): Expiration {
    const month = '0' + paymentInformation.expirationMonth;
    const year = paymentInformation.expirationYear || '';
    const last2 = 2;
    const expirationDetails: Expiration = {
      month: month.substring(month.length - last2),
      year: year.substring(year.length - last2),
    };
    return expirationDetails;
  }

  /**
   * Function to set Address
   * @returns Address
   */
  private setAddress(zipCode: string, countryCode: object, address: CardAddressModel): Address {
    let addressDetails: Address = {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    };
    if (!zipCode) {
      addressDetails = {
        addressLine1: address.address1 ? address.address1.value : '',
        addressLine2: address.address2 ? address.address2.value : '',
        city: address.city ? address.city.value : '',
        state: address.state ? address.state.value : '',
        zipCode: address.zipCode ? address.zipCode.value : '',
        country: address.country && address.country.value ? address.country.value.code : '',
      };
    } else {
      addressDetails.zipCode = zipCode;
      if (countryCode) {
        const code = 'code';
        addressDetails.country = countryCode[code];
      }
    }
    return addressDetails;
  }

  /**
   * used to set amount
   * @param {string} value
   */
  public setAmount(value: string): Amount {
    const amount = {
      value: parseFloat(value || '0'),
      currencyCode: SHARED_PAYMENT.CURRENCY,
    };
    return amount;
  }
  /**
   * used to mask card number for submit order request
   * @param {string} cardNumber
   */
  public maskCardNumber(cardNumber: string): string {
    let maskedCardNumber: string;
    const firstnumber = cardNumber.slice(0, SHARED_PAYMENT.SIX);
    const lastnumber = cardNumber.slice(SHARED_PAYMENT.SIX, cardNumber.length);
    return (maskedCardNumber = firstnumber + lastnumber.replace(/\d(?=\d{4})/g, '0'));
  }
}
